import { Directive, ElementRef, Inject, OnInit, Optional } from '@angular/core';
import { MatLegacyTooltip as MatTooltip } from '@angular/material/legacy-tooltip';

interface ArrayClassMatTooltip extends MatTooltip {
  tooltipClass: string[];
}

/**
 * The Material tooltip component does not support adding arrows to the tooltip container.
 * Setting the tooltipPosition does not guarantee that the tooltip will open in that position!
 *
 * This directive adds a class to the tooltip which indicates the current position of it.
 * It only adds up and down classes, left and right cases are not considered.
 */
@Directive({
  selector: '[tenTooltipPositionIndicator]',
  standalone: true,
})
export class TooltipPositionIndicatorDirective implements OnInit {
  private readonly ARROW_DOWN_CLASS = 'tooltip-arrow-down';
  private readonly ARROW_UP_CLASS = 'tooltip-arrow-up';

  constructor(@Optional() @Inject(MatTooltip) public tooltip: MatTooltip, private elRef: ElementRef) {}

  public ngOnInit() {
    if (!this.tooltip) {
      return;
    }

    // Ensure tooltipClass is array for easier handling, MatTooltip allows strings and Sets too
    if (typeof this.tooltip.tooltipClass === 'string') {
      this.tooltip.tooltipClass = this.tooltip.tooltipClass.split(' ');
    } else if (this.tooltip.tooltipClass instanceof Set) {
      this.tooltip.tooltipClass = Array.from(this.tooltip.tooltipClass);
    }

    const originalTooltipShowFn = this.tooltip.show;
    this.tooltip.show = (delay) => {
      originalTooltipShowFn.call(this.tooltip, delay);

      if (!this.tooltip.disabled) {
        setTimeout(() => {
          const hostElementTop = this.elRef.nativeElement.getBoundingClientRect().top;
          const tooltipElementTop = this.tooltip._overlayRef.overlayElement.getBoundingClientRect().top;

          if (hostElementTop > tooltipElementTop) {
            this.setArrowDown();
          } else {
            this.setArrowUp();
          }
        });
      }
    };
  }

  private setArrowUp() {
    this.addTooltipClass(this.ARROW_UP_CLASS);
    this.removeTooltipClass(this.ARROW_DOWN_CLASS);
  }

  private setArrowDown() {
    this.addTooltipClass(this.ARROW_DOWN_CLASS);
    this.removeTooltipClass(this.ARROW_UP_CLASS);
  }

  private addTooltipClass(classToAdd: string) {
    const tooltipClass = (<ArrayClassMatTooltip>this.tooltip).tooltipClass;

    if (!tooltipClass.includes(classToAdd)) {
      tooltipClass.push(classToAdd);
    }
  }

  private removeTooltipClass(classToRemove: string) {
    this.tooltip.tooltipClass = (<ArrayClassMatTooltip>this.tooltip).tooltipClass.filter(
      (clazz) => clazz !== classToRemove
    );
  }
}
